import api from '~/store/services/api';
import urlConstants from '~/lib/constants/url.constants';
import {
  CreateProblemRequest,
  CreateMultipleProblemsRequest,
  CreateMultipleProblemsResponse,
  CreateQoLGoalRequest,
  CreateCarePlanRequest,
  CreateCarePlanResponse,
  DeleteProblemRequest,
  DeleteQolGoalRequest,
  OrderedEnumeration,
  CarePlanResponse,
  UpdateGoalArgs,
  UpdateProblemArgs,
  UpdateInterventionArgs,
  UpdateManifestationArgs,
  UpdateQoLGoalArgs,
  UpdateCarePlanRequest,
  CarePlanNoteResponse,
  RevertGoalStatusOnNoteArgs,
  RevertGoalStatusOnNoteResponse,
  CarePlanVoidCustomInterventionArgs,
} from '~/lib/types/carePlan/carePlans';
import {
  CreateCarePlanCustomInterventionsRequest,
  CreateCarePlanCustomInterventionsResponse,
} from '~/lib/types/carePlan/customInterventions';
import { ENUMERATION_CACHE_TIME } from './enumerations';
import { CacheTag } from './CacheTag';
import { HttpRequestMethod } from '..';
import {
  filterOutVoidGoals,
  filterOutVoidInterventionsFromProblems,
  filterOutVoidQolGoals,
} from '~/lib/util/carePlan.util';

// Care plan instance data APIs
export const carePlanApi = api.injectEndpoints({
  endpoints: (build) => ({
    createProblem: build.mutation<void, CreateProblemRequest>({
      invalidatesTags: (_, __, { treatmentEpisodeId }) => [
        {
          id: treatmentEpisodeId,
          type: CacheTag.CarePlans,
        },
        CacheTag.CarePlanForNote,
      ],
      query: ({ carePlanId, problem }) => ({
        body: problem,
        method: HttpRequestMethod.POST,
        url: urlConstants.carePlans.createProblem(carePlanId),
      }),
    }),
    createMultipleProblems: build.mutation<
      CreateMultipleProblemsResponse,
      CreateMultipleProblemsRequest
    >({
      invalidatesTags: (_, __, { treatmentEpisodeId }) => [
        {
          id: treatmentEpisodeId,
          type: CacheTag.CarePlans,
        },
        CacheTag.CarePlanForNote,
      ],
      query: ({ carePlanId, problems }) => ({
        body: { problems },
        method: HttpRequestMethod.POST,
        url: urlConstants.carePlans.createMultipleProblems(carePlanId),
      }),
    }),
    createQoLGoal: build.mutation<void, CreateQoLGoalRequest>({
      invalidatesTags: (_, __, { treatmentEpisodeId }) => [
        {
          id: treatmentEpisodeId,
          type: CacheTag.CarePlans,
        },
        CacheTag.CarePlanForNote,
      ],
      query: ({ treatmentEpisodeId: _, carePlanId, ...body }) => ({
        body,
        method: HttpRequestMethod.POST,
        url: urlConstants.carePlans.createQoLGoal(carePlanId),
      }),
    }),
    createCustomInterventions: build.mutation<
      CreateCarePlanCustomInterventionsResponse,
      CreateCarePlanCustomInterventionsRequest & { treatmentEpisodeId: string }
    >({
      invalidatesTags: (_, __, { treatmentEpisodeId }) => [
        {
          id: treatmentEpisodeId,
          type: CacheTag.CarePlans,
        },
        CacheTag.CarePlanForNote,
      ],
      query: ({ treatmentEpisodeId: _, ...body }) => ({
        body,
        method: HttpRequestMethod.POST,
        url: urlConstants.carePlans.createCustomInterventions,
      }),
    }),
    createCarePlan: build.mutation<
      CreateCarePlanResponse,
      CreateCarePlanRequest
    >({
      invalidatesTags: (_, __, { treatmentEpisodeId }) => [
        {
          id: treatmentEpisodeId,
          type: CacheTag.CarePlans,
        },
        CacheTag.CarePlanForNote,
      ],
      query: ({ treatmentEpisodeId, ...body }) => ({
        body,
        method: HttpRequestMethod.POST,
        url: urlConstants.carePlans.createCarePlan(treatmentEpisodeId),
      }),
    }),
    updateCarePlan: build.mutation<void, UpdateCarePlanRequest>({
      invalidatesTags: (_, __, { treatmentEpisodeId }) => [
        {
          id: treatmentEpisodeId,
          type: CacheTag.CarePlans,
        },
        CacheTag.CarePlanForNote,
      ],
      query: ({ treatmentEpisodeId, carePlanId, statusCode }) => ({
        body: {
          status: statusCode,
        },
        method: HttpRequestMethod.PUT,
        url: urlConstants.carePlans.updateCarePlan(
          treatmentEpisodeId,
          carePlanId,
        ),
      }),
    }),
    deleteProblem: build.mutation<void, DeleteProblemRequest>({
      invalidatesTags: (_, __, { treatmentEpisodeId }) => [
        {
          id: treatmentEpisodeId,
          type: CacheTag.CarePlans,
        },
        CacheTag.CarePlanForNote,
      ],
      query: ({ problemId }) => ({
        body: {},
        method: HttpRequestMethod.DELETE,
        url: urlConstants.carePlans.deleteProblem(problemId),
      }),
    }),
    deleteQolGoal: build.mutation<void, DeleteQolGoalRequest>({
      invalidatesTags: (_, __, { treatmentEpisodeId }) => [
        {
          id: treatmentEpisodeId,
          type: CacheTag.CarePlans,
        },
        CacheTag.CarePlanForNote,
      ],
      query: ({ goalId }) => ({
        body: {},
        method: HttpRequestMethod.DELETE,
        url: urlConstants.carePlans.deleteQolGoal(goalId),
      }),
    }),
    getGoalStatuses: build.query<OrderedEnumeration[], void>({
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
      query: () => urlConstants.carePlans.getGoalStatusesEnum,
    }),
    getCarePlansByTreatmentEpisodeId: build.query<CarePlanResponse[], string>({
      providesTags: (_, __, treatmentEpisodeId) => [
        {
          id: treatmentEpisodeId,
          type: CacheTag.CarePlans,
        },
        CacheTag.CarePlanForNote,
      ],
      query: (treatmentEpisodeId) => ({
        url: urlConstants.carePlans.getCarePlans(treatmentEpisodeId),
      }),
      transformResponse: (response: CarePlanResponse[]) => {
        return response.map((carePlan) => ({
          ...carePlan,
          problems: filterOutVoidInterventionsFromProblems(
            filterOutVoidGoals(carePlan.problems),
          ),
          qolGoals: filterOutVoidQolGoals(carePlan.qolGoals),
        }));
      },
    }),
    getCarePlanForNote: build.query<
      CarePlanNoteResponse | null, // null if no care plan exists
      {
        providerRole: string;
        noteId: string;
        // the template selected in the editor, which can be ahead of the note's saved template
        noteTemplateRevisionId?: string;
      }
    >({
      providesTags: (_, __, { noteId }) => [
        { id: noteId, type: CacheTag.CarePlanForNote },
      ],
      query: ({ providerRole, noteId, noteTemplateRevisionId }) => ({
        url: urlConstants.carePlans.getCarePlanForNote(noteId),
        params: {
          'provider-role': providerRole,
          'note-template-revision-id': noteTemplateRevisionId,
        },
      }),
      keepUnusedDataFor: 5 * 60,
      transformResponse: (response: CarePlanNoteResponse | null) => {
        if (!response) {
          return response;
        }

        return {
          ...response,
          problems: filterOutVoidInterventionsFromProblems(
            filterOutVoidGoals(response.problems),
          ),
          qolGoals: filterOutVoidQolGoals(response.qolGoals),
        };
      },
    }),
    updateGoal: build.mutation<{ id: string }, UpdateGoalArgs>({
      invalidatesTags: (_, __, { treatmentEpisodeId, body }) => [
        {
          id: treatmentEpisodeId,
          type: CacheTag.CarePlans,
        },
        ...(body.noteExternalId
          ? [{ id: body.noteExternalId, type: CacheTag.CarePlanForNote }]
          : [CacheTag.CarePlanForNote]),
      ],
      query: (req) => ({
        body: req.body,
        method: HttpRequestMethod.PUT,
        url: urlConstants.carePlans.updateGoal(req.goalId),
      }),
    }),
    updateQoLGoal: build.mutation<void, UpdateQoLGoalArgs>({
      invalidatesTags: (_, __, { treatmentEpisodeId, body }) => [
        {
          id: treatmentEpisodeId,
          type: CacheTag.CarePlans,
        },
        ...(body.noteExternalId
          ? [{ id: body.noteExternalId, type: CacheTag.CarePlanForNote }]
          : [CacheTag.CarePlanForNote]),
      ],
      query: ({ treatmentEpisodeId: _, goalId, body }) => ({
        body,
        method: HttpRequestMethod.PUT,
        url: urlConstants.carePlans.updateQoLGoal(goalId),
      }),
    }),
    revertGoalStatusOnNote: build.mutation<
      RevertGoalStatusOnNoteResponse,
      RevertGoalStatusOnNoteArgs
    >({
      invalidatesTags: [CacheTag.CarePlanForNote],
      query: ({ goalId, noteExternalId }) => ({
        body: {},
        method: HttpRequestMethod.PUT,
        url: urlConstants.carePlans.revertGoalStatusOnNote(
          goalId,
          noteExternalId,
        ),
      }),
    }),
    revertQoLGoalStatusOnNote: build.mutation<
      RevertGoalStatusOnNoteResponse,
      RevertGoalStatusOnNoteArgs
    >({
      invalidatesTags: [CacheTag.CarePlanForNote],
      query: ({ goalId, noteExternalId }) => ({
        body: {},
        method: HttpRequestMethod.PUT,
        url: urlConstants.carePlans.revertQoLGoalStatusOnNote(
          goalId,
          noteExternalId,
        ),
      }),
    }),
    updateProblem: build.mutation<void, UpdateProblemArgs>({
      invalidatesTags: (_, __, { treatmentEpisodeId, body }) => [
        {
          id: treatmentEpisodeId,
          type: CacheTag.CarePlans,
        },
        ...(body.noteExternalId
          ? [{ id: body.noteExternalId, type: CacheTag.CarePlanForNote }]
          : [CacheTag.CarePlanForNote]),
      ],
      query: ({ treatmentEpisodeId: _, problemId, body }) => ({
        body,
        method: HttpRequestMethod.PUT,
        url: urlConstants.carePlans.updateProblem(problemId),
      }),
    }),
    updateIntervention: build.mutation<void, UpdateInterventionArgs>({
      invalidatesTags: (_, __, { treatmentEpisodeId, body }) => [
        {
          id: treatmentEpisodeId,
          type: CacheTag.CarePlans,
        },
        ...(body?.noteExternalId
          ? [{ id: body.noteExternalId, type: CacheTag.CarePlanForNote }]
          : [CacheTag.CarePlanForNote]),
      ],
      query: ({ treatmentEpisodeId: _, interventionId, body }) => ({
        body,
        method: HttpRequestMethod.PATCH,
        url: urlConstants.carePlans.updateIntervention(interventionId),
      }),
    }),
    voidCustomIntervention: build.mutation<
      void,
      CarePlanVoidCustomInterventionArgs
    >({
      invalidatesTags: (_, __, { treatmentEpisodeId }) => [
        {
          id: treatmentEpisodeId,
          type: CacheTag.CarePlans,
        },
        CacheTag.CarePlanForNote,
      ],
      query: ({ interventionId, voidReasonCode }) => ({
        body: { voidReasonCode },
        method: HttpRequestMethod.POST,
        url: urlConstants.carePlans.voidCustomIntervention(interventionId),
      }),
    }),
    updateManifestation: build.mutation<
      { id: string },
      UpdateManifestationArgs
    >({
      invalidatesTags: (_, __, { treatmentEpisodeId, body }) => [
        {
          id: treatmentEpisodeId,
          type: CacheTag.CarePlans,
        },
        {
          id: body.noteExternalId,
          type: CacheTag.CarePlanForNote,
        },
      ],
      query: ({ treatmentEpisodeId: _, manifestationId, body }) => ({
        body,
        method: HttpRequestMethod.PATCH,
        url: urlConstants.carePlans.updateManifestation(manifestationId),
      }),
    }),
  }),
});

export const {
  useCreateProblemMutation,
  useCreateMultipleProblemsMutation,
  useCreateQoLGoalMutation,
  useCreateCustomInterventionsMutation,
  useCreateCarePlanMutation,
  useUpdateCarePlanMutation,
  useGetCarePlansByTreatmentEpisodeIdQuery,
  useGetCarePlanForNoteQuery,
  useDeleteProblemMutation,
  useUpdateGoalMutation,
  useUpdateProblemMutation,
  useDeleteQolGoalMutation,
  useUpdateInterventionMutation,
  useVoidCustomInterventionMutation,
  useUpdateManifestationMutation,
  useGetGoalStatusesQuery,
  useUpdateQoLGoalMutation,
  useRevertGoalStatusOnNoteMutation,
  useRevertQoLGoalStatusOnNoteMutation,
} = carePlanApi;
